import React, { ReactElement } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  getFractionCount,
  getRentalYieldPerMonth,
  getMemoizedProperties,
  getMemoizedActiveProperties,
  getMemoizedCompletedProperties,
  getTotalPortfolioValue,
  useSelector,
  getPropertiesLoaded,
  getNftLoaded,
  getWallet,
} from '../../store'
import PropertiesCard from '../../components/dashboard/properties/PropertiesCard'
import PropertiesMapCard from '../../components/dashboard/propertyMap/PropertiesMapCard'
import FractionsCard from '../../components/dashboard/userStats/FractionsCard'
import PortfolioCard from '../../components/dashboard/userStats/PortfolioCard'
import MonthlyYieldsCard from '../../components/dashboard/userStats/MonthlyYieldsCard'
import { NextSeo } from 'next-seo'
import { useTranslation } from 'next-i18next'
import NoDashboardContent from '../../components/dashboard/NoDashboardContent'
import Layout from '../../components/Layout'
import PayoutsReviewCard from '../../components/dashboard/payouts/payoutsReview/PayoutReviewCard'
import { PropertyWithAccess } from '../../types/Property/Property'

function DashboardIndexPage() {
  const { t } = useTranslation('dashboard')
  const properties = useSelector(getMemoizedProperties)
  const activeProperties = useSelector(getMemoizedActiveProperties)
  const completedProperties = useSelector(getMemoizedCompletedProperties)
  const fractionCount = useSelector(getFractionCount)
  const propertyCount = activeProperties.length
  const currentMonthlyYields = useSelector(getRentalYieldPerMonth)
  const totalPortfolioValue = useSelector(getTotalPortfolioValue)
  const wallet = useSelector(getWallet)

  const propertiesLoaded = useSelector(getPropertiesLoaded)
  const nftLoaded = useSelector(getNftLoaded)

  if (wallet && (!propertiesLoaded || !nftLoaded)) {
    return null
  }

  if (!wallet || (!properties.length && propertiesLoaded && nftLoaded)) {
    return (
      <div className="relative">
        <NextSeo
          title={t('seo.title') || ''}
          description={t('seo.description') || ''}
        />
        <NoDashboardContent />
      </div>
    )
  }

  return (
    <div className="flex grow flex-col">
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />
      <div className="flex grow flex-col">
        <div className="space-y-3 md:flex md:space-x-3 md:space-y-0">
          <div className="h-full md:w-1/3">
            <PortfolioCard currentPropertiesAmount={propertyCount} />
          </div>
          <div className="h-full md:w-1/3">
            <FractionsCard
              currentFractionAmount={fractionCount}
              totalPortfolioValue={totalPortfolioValue}
            />
          </div>
          <div className="h-full md:w-1/3">
            <MonthlyYieldsCard currentMonthlyYields={currentMonthlyYields} />
          </div>
        </div>

        <div className="mt-3 flex w-full grow flex-col">
          <PayoutsReviewCard />
        </div>

        <div className="row-span-2 mt-3 grid h-full grid-cols-1 gap-4 2xl:grid-cols-3">
          <div className="col-span-1 flex flex-col gap-4 2xl:col-span-2">
            <PropertiesCard
              classname="col-span-1"
              properties={activeProperties as PropertyWithAccess[]}
              title={t('propertyTab.title')}
              emptyMessage={t('propertyTab.empty')}
            />
            {completedProperties.length > 0 && (
              <PropertiesCard
                classname="col-span-1"
                properties={completedProperties as PropertyWithAccess[]}
                title={t('completedProjects.title')}
                emptyMessage={t('completedProjects.empty')}
              />
            )}
          </div>
          <div className="col-span-1 h-96 2xl:h-auto">
            <PropertiesMapCard
              properties={properties as PropertyWithAccess[]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

DashboardIndexPage.getLayout = (page: ReactElement) => {
  return <Layout>{page}</Layout>
}

export default DashboardIndexPage

export async function getServerSideProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'dashboard'])),
    },
  }
}
